import { IRcaTest } from "../models/IRcaTest";
import { IRcaTestCategory } from "../models/IRcaTestCategory";
import { Utils } from "../Utils";
import { ITestInputForm } from "../models/ITestInputForm";
import { JsonService } from "./JsonService";
import { FetchHttpClient, IHttpClient } from "./HttpClient";
import { IOAuthCredential } from "../models/IOAuthCredential";

/**
 * The test catalog. Used to fetch the available tests
 */
export interface ITestCatalog {
  /**
   * Gets the set of test categories
   *
   * @param language - The language for which the categories should be loaded
   * @returns - The test categories
   */
  getTestCategoriesAsync(language: string): Promise<IRcaTestCategory[]>;

  /**
   * Gets the list of tests for a given category
   *
   * @param language - The language for which the tests should be loaded
   * @param category - The category for which tests should be loaded
   * @returns - The tests
   */
  getTestsAsync(language: string, category: string): Promise<IRcaTest[]>;

  /**
   * Gets the input form for a particular connectivity test
   *
   * @param language - The language
   * @param testId - The test Id
   * @returns - The test input form
   */
  getInputFormAsync(language: string, testId: string): Promise<ITestInputForm>;

  /**
   * Executes a test
   *
   * @param language - the user's language
   * @param testId - the test id to execute
   * @param parameters - the test parameters
   * @param antiForgeryToken - The anti-forgery token
   * @returns the execution response
   */
  executeTestAsync(
    language: string,
    testId: string,
    parameters: Record<string, unknown>,
    antiForgeryToken: string
  ): Promise<ITestExecuteResponse>;

  /**
   * Executes a test with authentication
   *
   * @param language - the user's language
   * @param testId - the test id to execute
   * @param parameters - the test parameters
   * @param antiForgeryToken - The anti-forgery token
   * @param credential - the OAuth credential
   * @returns the execution response
   */
  executeTestWithAuthAsync(
    language: string,
    testId: string,
    parameters: Record<string, unknown>,
    antiForgeryToken: string,
    credential: IOAuthCredential
  ): Promise<ITestExecuteResponse>;
}

/**
 * Data contract from the server for the /api/tests API
 */
export interface ITestListWithCategories {
  /**
   * The list of all tests
   */
  tests: IRcaTest[];

  /**
   * The test categories
   */
  categories: IRcaTestCategory[];
}

/**
 * Data contract from the server for the /api/tests/id/execute method
 */
export interface ITestExecuteResponse {
  /**
   * The test execution status
   */
  status: "Success" | "UserUnknown" | "Throttled" | "ValidationError";

  /**
   * The execution id that can later be used to get the result
   */
  executionId: string;

  /**
   * An error message if one is encountered
   */
  error: string;

  /**
   * Any validation errors encountered
   */
  validationErrors: { [fieldName: string]: string };

  /**
   * A HIP Validation error if encountered
   */
  hipValidationError?: number | null;
}

/**
 * The test catalog. Used to fetch the available tests
 */
export class TestCatalog extends JsonService implements ITestCatalog {
  /**
   * The language for which tests have been cached
   */
  private cachedLanguage = "";

  /**
   * The cache of all test categories
   */
  private categoryCache: IRcaTestCategory[] = [];

  /**
   * The cache of all tests organized by category
   */
  private testCache: { [category: string]: IRcaTest[] } = {};

  /**
   * Initializes a new instance of the TestCatalog class.
   *
   * @param apiVersion - The API version
   * @param httpClient - The optional HTTP client
   */
  public constructor(apiVersion: string, httpClient: IHttpClient | null = null) {
    super(apiVersion, httpClient ?? new FetchHttpClient());
  }

  /**
   * Gets the set of test categories
   *
   * @param language - The language for which the categories should be loaded
   * @returns - The test categories
   */
  public async getTestCategoriesAsync(language: string): Promise<IRcaTestCategory[]> {
    if (!this.categoryCache.length || this.cachedLanguage !== language) {
      await this.refreshTestsAsync(language);
    }

    return this.categoryCache;
  }

  /**
   * Gets the list of tests for a given category
   *
   * @param language - The language for which the tests should be loaded
   * @param category - The category for which tests should be loaded
   * @returns - The tests
   */
  public async getTestsAsync(language: string, category: string): Promise<IRcaTest[]> {
    if (!this.testCache[category] || this.cachedLanguage !== language) {
      await this.refreshTestsAsync(language);
    }

    return this.testCache[category] ?? [];
  }

  /**
   * Gets the input form for a particular connectivity test
   *
   * @param language - The language
   * @param testId - The test Id
   * @returns The input form
   */
  public getInputFormAsync(language: string, testId: string): Promise<ITestInputForm> {
    return this.getJsonAsync(`/api/${this.apiVersion}/tests/${encodeURIComponent(testId)}/input?lang=${encodeURIComponent(language)}`);
  }

  /**
   * Executes a test
   *
   * @param language - the user's language
   * @param testId - the test id to execute
   * @param parameters - the test parameters
   * @param antiForgeryToken - The anti-forgery token
   * @returns the execution response
   */
  public executeTestAsync(
    language: string,
    testId: string,
    parameters: Record<string, unknown>,
    antiForgeryToken: string
  ): Promise<ITestExecuteResponse> {
    return this.postJsonAsync(
      `/api/${this.apiVersion}/tests/${encodeURIComponent(testId)}/execute?lang=${encodeURIComponent(language)}`,
      antiForgeryToken,
      parameters
    );
  }

  /**
   * Executes a test with authentication
   *
   * @param language - the user's language
   * @param testId - the test id to execute
   * @param parameters - the test parameters
   * @param antiForgeryToken - The anti-forgery token
   * @param credential - The OAuth credential
   * @returns the execution response
   */
  public executeTestWithAuthAsync(
    language: string,
    testId: string,
    parameters: Record<string, unknown>,
    antiForgeryToken: string,
    credential: IOAuthCredential
  ): Promise<ITestExecuteResponse> {
    return this.postJsonAsync(
      `/api/${this.apiVersion}/tests/${encodeURIComponent(testId)}/executewithauth?lang=${encodeURIComponent(language)}`,
      antiForgeryToken,
      parameters,
      credential.accessToken
    );
  }

  /**
   * Refreshes the test and category cache from the server
   *
   * @param language - The language for which to reload the tests
   */
  private async refreshTestsAsync(language: string): Promise<void> {
    const data = await this.getJsonAsync<ITestListWithCategories>(`/api/${this.apiVersion}/tests?lang=${encodeURIComponent(language)}`);
    this.categoryCache = data.categories;
    this.testCache = Utils.groupBy(data.tests, (t) => t.categoryId);
    this.cachedLanguage = language;
  }
}
